/**
 * EditableContentDual.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { useToast } from './ToastContainer';
import { useTheme } from '../context/ThemeContext';
import AssetSearchModal from './AssetSearchModal';
import LinkPickerModal from './LinkPickerModal';
import TemplateInsertModal from './TemplateInsertModal';
import HelpPopup from './HelpPopup';
import { PAGE_TEMPLATES } from '../templates/pageTemplates'; // fallback if API unavailable
import { filterInsertablePageTemplates } from '../utils/pageTemplateKeys';
import './EditableContentDual.css';

const STYLE_OPTIONS = [
  { label: 'Bold', snippet: '<b>Bold</b>', example: '<b>Bold</b>', tooltip: '<b>Bold</b>', desc: 'Bold text' },
  { label: 'Italic', snippet: '<i>Italic</i>', example: '<i>Italic</i>', tooltip: '<i>Italic</i>', desc: 'Italic text' },
  { label: 'Link', snippet: '<a href="/">link</a>', example: '<a href="/">link</a>', tooltip: '<a href="/">link</a>', desc: 'Clickable link' },
  { label: 'List', snippet: '<ul>\n  <li>Item</li>\n</ul>', example: '<ul style="display:inline-block;margin:0"><li>Item</li></ul>', tooltip: '<ul>\n  <li>Item</li>\n</ul>', desc: 'Unordered list' },
  { label: 'Heading', snippet: '<h2>Heading</h2>', example: '<h2 style="display:inline">Heading</h2>', tooltip: '<h2>Heading</h2>', desc: 'Section heading' },
  { label: 'Rounded Image', snippet: '<img src="/images/ReceivingMcLennan.jpg" alt="Rounded" style="border-radius:8px;" />', example: '<img src="/images/ReceivingMcLennan.jpg" alt="Rounded" style="border-radius:8px;width:40px;height:40px;object-fit:cover;" />', tooltip: '<img src="/images/ReceivingMcLennan.jpg" alt="Rounded" style="border-radius:8px;" />', desc: 'Image with rounded corners' },
  { label: 'Shadow', snippet: '<img src="/images/ReceivingMcLennan.jpg" alt="Shadow" style="box-shadow:0 4px 16px rgba(0,0,0,0.15);width:40px;height:40px;object-fit:cover;" />', example: '<img src="/images/ReceivingMcLennan.jpg" alt="Shadow" style="box-shadow:0 4px 16px rgba(0,0,0,0.15);width:40px;height:40px;object-fit:cover;" />', tooltip: '<img src="/images/ReceivingMcLennan.jpg" alt="Shadow" style="box-shadow:0 4px 16px rgba(0,0,0,0.15);" />', desc: 'Image with drop shadow' },
  { label: 'Highlight Text', snippet: '<span class="highlight">Important</span>', example: '<span class="highlight">Important</span>', tooltip: '<span class="highlight">Important</span>', desc: 'Highlighted text' },
  { label: 'Subtitle', snippet: '<p class="subtitle">Subtitle text</p>', example: '<span class="subtitle">Subtitle text</span>', tooltip: '<p class="subtitle">Subtitle text</p>', desc: 'Subtitle or subheading' },
  { label: 'Center Content', snippet: '<div style="display:block;margin:0 auto;text-align:center;">Centered content</div>', example: '<div style="display:block;margin:0 auto;text-align:center;">Centered content</div>', tooltip: '<div style="display:block;margin:0 auto;text-align:center;">Centered content</div>', desc: 'Center-aligned block' },
];

function validateHtml(html) {
  const open = (html.match(/</g) || []).length;
  const close = (html.match(/>/g) || []).length;
  if (open !== close) return 'Unbalanced < and > brackets.';
  if (/<script/i.test(html)) return 'Warning: <script> tags are not allowed.';
  return '';
}

// Helper to ensure proper path
function withBasePath(path) {
  if (path.startsWith('/')) return path;
  return `/${path}`;
}

// Helper for rendering dropdown item with preview on the left
function DropdownItemWithPreview({ label, example, tooltip, desc, onClick }) {
  // If example contains src or href, replace withBasePath
  let safeExample = example;
  if (typeof example === 'string') {
    safeExample = example.replace(/src="(\/[^"']*)"/g, (m, p1) => `src="${withBasePath(p1)}"`)
      .replace(/href="(\/[^"']*)"/g, (m, p1) => `href="${withBasePath(p1)}"`);
  }
  return (
    <div className="dropdown-item" onClick={onClick} title={tooltip}>
      <span className="dropdown-preview" style={{ marginRight: '1.5rem' }} dangerouslySetInnerHTML={{ __html: safeExample }} />
      <span className="dropdown-label-group">
        <span className="dropdown-label-main">{label}</span>
        {desc && <span className="dropdown-label-desc">{desc}</span>}
      </span>
    </div>
  );
}

const EditableContentDual = ({ initialHtml, canEdit, pageId = 'default', pageFlags = null, onContentChange = null }) => {
  const { showSuccess, showError } = useToast();
  const { theme, currentTheme } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [contentHtml, setContentHtml] = useState('');
  const [simpleEditorContent, setSimpleEditorContent] = useState(''); // Separate state for simple editor
  const [isSaving, setIsSaving] = useState(false);
  const [editorMode, setEditorMode] = useState('simple'); // 'simple' or 'tiptap'
  const [validationMsg, setValidationMsg] = useState('');
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [showStyleDropdown, setShowStyleDropdown] = useState(false);
  const [showAssetSearchModal, setShowAssetSearchModal] = useState(false);
  const [assetSearchType, setAssetSearchType] = useState(null); // 'image' or 'music'
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [apiTemplates, setApiTemplates] = useState(null); // null = not loaded yet
  const [templateSource, setTemplateSource] = useState('loading'); // 'loading' | 'db' | 'static'
  const [pageMetadata, setPageMetadata] = useState(null);
  const editorRef = useRef(null);
  const textareaRef = useRef(null);
  const styleDropdownRef = useRef(null);
  const templateDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close style dropdown if clicking outside
      if (showStyleDropdown && styleDropdownRef.current && !styleDropdownRef.current.contains(event.target)) {
        setShowStyleDropdown(false);
      }
      // Close template dropdown if clicking outside
      if (showTemplateDropdown && templateDropdownRef.current && !templateDropdownRef.current.contains(event.target)) {
        setShowTemplateDropdown(false);
      }
    };

    // Use mousedown instead of click to prevent issues with text selection
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStyleDropdown, showTemplateDropdown]);

  // Fetch templates from the DB; fall back to static PAGE_TEMPLATES if unavailable
  useEffect(() => {
    let cancelled = false;
    setTemplateSource('loading');
    fetch('/api/content/templates?for=editor')
      .then(r => r.ok ? r.json() : Promise.reject(new Error('fetch failed')))
      .then(rows => {
        if (cancelled) return;
        const insertable = filterInsertablePageTemplates(rows || []);
        if (insertable.length > 0) {
          // Convert array → object keyed by template_key (matches PAGE_TEMPLATES shape)
          const byKey = {};
          insertable.forEach(t => { byKey[t.template_key] = t; });
          setApiTemplates(byKey);
          setTemplateSource('db');
        } else {
          // DB is empty — not seeded yet, use static fallback
          setTemplateSource('static');
        }
      })
      .catch(() => {
        if (!cancelled) setTemplateSource('static');
      });
    return () => { cancelled = true; };
  }, []);

  // Load saved content from API or use initial HTML
  useEffect(() => {
    const loadContent = async () => {
      console.log(`EditableContentDual: Loading content for pageId: ${pageId}`);

      try {
        const response = await fetch(`/api/content/pages/${pageId}`);
        console.log(`EditableContentDual: API response status:`, response.status);

        if (response.ok) {
          const data = await response.json();
          console.log(`EditableContentDual: API data:`, data);

          // Store page metadata to preserve it during saves
          setPageMetadata({
            title: data.title,
            description: data.description,
            pageType: data.pageType,
            metadata: data.metadata,
            featured: data.featured,
            home_announcement: data.home_announcement,
            is_hidden: data.is_hidden,
          });

          // Check if saved content is empty or just contains empty tags
          const savedContent = data.html || '';
          const isEmptyContent = !savedContent ||
            savedContent.trim() === '' ||
            savedContent.trim() === '<p></p>' ||
            savedContent.trim() === '<p><br></p>' ||
            savedContent.trim() === '<br>';

          if (isEmptyContent) {
            console.log(`EditableContentDual: Saved content is empty, using initial HTML`);
            const content = initialHtml || '';
            setContentHtml(content);
            setSimpleEditorContent(content);
          } else {
            console.log(`EditableContentDual: Using saved content from database`);
            setContentHtml(savedContent);
            setSimpleEditorContent(savedContent);
          }
        } else if (response.status === 404) {
          // No saved content yet, use initial HTML
          console.log(`EditableContentDual: No saved content, using initial HTML`);
          const content = initialHtml || '';
          setContentHtml(content);
          setSimpleEditorContent(content);
          setPageMetadata(null);
        } else {
          console.error('Error loading page content:', response.statusText);
          const content = initialHtml || '';
          setContentHtml(content);
          setSimpleEditorContent(content);
          setPageMetadata(null);
        }
      } catch (error) {
        console.error('Error loading page content:', error);
        // Fallback to initial HTML if API fails
        console.log(`EditableContentDual: API failed, using initial HTML`);
        const content = initialHtml || '';
        setContentHtml(content);
        setSimpleEditorContent(content);
        setPageMetadata(null);
      }
    };

    loadContent();
    // Only reload when the page id changes — NOT when parent html state updates
    // (live onContentChange would re-fetch and reset the caret / wipe keystrokes).
  }, [pageId]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally omit initialHtml

  useEffect(() => {
    setValidationMsg(validateHtml(contentHtml));
  }, [contentHtml]);

  // Tiptap editor configuration
  const tiptapEditor = useEditor({
    extensions: [
      StarterKit.configure({
        link: {
          openOnClick: false,
          HTMLAttributes: {
            class: 'text-blue-600 underline',
          },
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextStyle,
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder: 'Start writing...',
      }),
    ],
    content: contentHtml,
    onUpdate: ({ editor }) => {
      // Only update contentHtml if we're in Tiptap mode
      if (editorMode === 'tiptap') {
        setContentHtml(editor.getHTML());
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
        style: `color: ${theme.colors['--color-text-primary']}; background: ${theme.colors['--color-background']};`,
      },
    },
  });

  // Update Tiptap content when contentHtml changes (only in Tiptap mode)
  useEffect(() => {
    if (tiptapEditor && editorMode === 'tiptap' && contentHtml !== tiptapEditor.getHTML()) {
      tiptapEditor.commands.setContent(contentHtml);
    }
  }, [contentHtml, tiptapEditor, editorMode]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancel = async () => {
    // Reload from API or revert to initial
    try {
      const response = await fetch(`/api/content/pages/${pageId}`);

      if (response.ok) {
        const data = await response.json();
        setContentHtml(data.html || initialHtml || '');
        setSimpleEditorContent(data.html || initialHtml || '');
        // Refresh metadata
        setPageMetadata({
          title: data.title,
          description: data.description,
          pageType: data.pageType,
          metadata: data.metadata
        });
      } else {
        setContentHtml(initialHtml || '');
        setSimpleEditorContent(initialHtml || '');
        setPageMetadata(null);
      }
    } catch (error) {
      console.error('Error reloading content:', error);
      setContentHtml(initialHtml || '');
      setSimpleEditorContent(initialHtml || '');
      setPageMetadata(null);
    }
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Prefer the active editor buffer
      let editorContent;
      if (editorMode === 'simple') {
        editorContent = simpleEditorContent;
      } else if (editorMode === 'tiptap' && tiptapEditor) {
        editorContent = tiptapEditor.getHTML();
      } else if (editorMode === 'tinymce' && editorRef.current) {
        editorContent = editorRef.current.getContent();
      } else {
        editorContent = contentHtml;
      }

      // Get JWT token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        showError('You must be logged in to save content');
        setIsSaving(false);
        return;
      }

      // Prepare save data with preserved metadata
      const saveData = {
        html: editorContent
      };

      // Include existing metadata and page flags (pin / home / hidden).
      // pageFlags from parent wins so title/meta edits in PageEditor are not overwritten.
      const flags = { ...(pageMetadata || {}), ...(pageFlags || {}) };
      if (Object.keys(flags).length) {
        if (flags.title !== undefined) saveData.title = flags.title;
        if (flags.description !== undefined) saveData.description = flags.description;
        if (flags.pageType !== undefined || flags.type !== undefined) {
          saveData.type = flags.pageType || flags.type;
        }
        if (flags.metadata !== undefined) {
          saveData.metadata =
            typeof flags.metadata === 'string' ? flags.metadata : JSON.stringify(flags.metadata);
        }
        if (flags.featured !== undefined) saveData.featured = flags.featured;
        if (flags.home_announcement !== undefined) saveData.home_announcement = flags.home_announcement;
        if (flags.is_hidden !== undefined) saveData.is_hidden = flags.is_hidden;
      }

      // Save to database via API
      const response = await fetch(`/api/content/pages/${pageId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(saveData)
      });

      if (response.ok) {
        const data = await response.json();
        setContentHtml(editorContent);
        setSimpleEditorContent(editorContent);
        if (typeof onContentChange === 'function') {
          onContentChange(editorContent);
        }
        setIsEditing(false);
        showSuccess(`Content saved successfully! (Version ${data.version})`);
      } else if (response.status === 401) {
        showError('You must be logged in to save content');
      } else if (response.status === 403) {
        showError('You do not have permission to edit content');
      } else {
        const errorData = await response.json();
        showError(errorData.error || 'Failed to save content');
      }
    } catch (error) {
      console.error('Error saving content:', error);
      showError('Failed to save content. Please check your connection.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleModeChange = (mode) => {
    if (mode === 'simple' && editorMode === 'tiptap') {
      // Switching from Tiptap to Simple - sync content
      setSimpleEditorContent(contentHtml);
    } else if (mode === 'tiptap' && editorMode === 'simple') {
      // Switching from Simple to Tiptap - sync content
      setContentHtml(simpleEditorContent);
    }
    setEditorMode(mode);
  };

  const handleTextareaChange = (e) => {
    const value = e.target.value;
    setSimpleEditorContent(value);
    // Also update contentHtml for preview and saving
    setContentHtml(value);
  };

  // Insert at cursor helper
  const insertAtCursor = (snippet) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      const newContent = simpleEditorContent + snippet;
      setSimpleEditorContent(newContent);
      setContentHtml(newContent);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = simpleEditorContent.substring(0, start);
    const after = simpleEditorContent.substring(end);
    const newContent = before + snippet + after;
    setSimpleEditorContent(newContent);
    setContentHtml(newContent);
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + snippet.length;
    }, 0);
  };

  const handleAssetSelect = (asset) => {
    // Use relative URLs for Docker/production compatibility
    if (asset.file_type === 'image') {
      const imageHtml = `<img src="/api/assets/${asset.id}/download" alt="${asset.alt_text || asset.original_filename}" style="max-width:100%;" />`;
      insertAtCursor(imageHtml);
    } else if (asset.file_type === 'music') {
      const fileName = asset.original_filename.replace(/\.[^/.]+$/, '');
      const musicHtml = `
<audio controls>
  <source src="/api/assets/${asset.id}/download" type="${asset.mime_type}">
  Your browser does not support the audio element.
</audio>
<p><a href="/api/assets/${asset.id}/download" download="${asset.original_filename}">Download ${fileName}</a></p>`;
      insertAtCursor(musicHtml);
    } else if (asset.file_type === 'video') {
      const fileName = asset.original_filename.replace(/\.[^/.]+$/, '');
      const videoHtml = `
<video width="400" controls>
  <source src="/api/assets/${asset.id}/download" type="${asset.mime_type}">
  Your browser does not support HTML video.
</video>
<p><a href="/api/assets/${asset.id}/download" download="${asset.original_filename}">Download ${fileName}</a></p>`;
      insertAtCursor(videoHtml);
    }
  };

  // Link picker modal insert
  const handleLinkPickerInsert = (url, text) => {
    // If url is null, text contains raw HTML (e.g., product with image)
    if (url === null) {
      insertAtCursor(text);
    } else {
      insertAtCursor(`<a href="${url}">${text}</a>`);
    }
    setShowLinkPicker(false);
  };

  // Insert style/class at cursor
  const handleStyleInsert = (snippet) => {
    let safeSnippet = snippet;
    if (typeof snippet === 'string') {
      safeSnippet = snippet.replace(/src="(\/[^"']*)"/g, (m, p1) => `src="${withBasePath(p1)}"`)
        .replace(/href="(\/[^"']*)"/g, (m, p1) => `href="${withBasePath(p1)}"`);
    }
    insertAtCursor(safeSnippet);
    setShowStyleDropdown(false);
  };

  // Template handlers — prefer DB templates, fall back to static (page HTML only, not email)
  const activeTemplates = filterInsertablePageTemplates(apiTemplates || PAGE_TEMPLATES);

  const handleTemplateInsert = (templateKey) => {
    const template = activeTemplates[templateKey];
    if (template) {
      setSelectedTemplate(template);
      setShowTemplateModal(true);
      setShowTemplateDropdown(false);
    }
  };

  const handleTemplateModalInsert = (html) => {
    insertAtCursor(html);
    setShowTemplateModal(false);
    setSelectedTemplate(null);
    showSuccess('Template inserted successfully!');
  };

  const handleTemplateModalCancel = () => {
    setShowTemplateModal(false);
    setSelectedTemplate(null);
  };

  return (
    <div className="editable-content-dual-container">
      {isEditing ? (
        <div className={`dual-editor-container theme-${currentTheme}`}>
          <div className="editor-header">
            <h3>✏️ Editing: {pageId}</h3>
            <div className="editor-mode-selector">
              <button
                className={`mode-btn ${editorMode === 'simple' ? 'active' : ''}`}
                onClick={() => handleModeChange('simple')}
              >
                📝 Simple Editor
              </button>
              {/* <button
                className={`mode-btn ${editorMode === 'tiptap' ? 'active' : ''}`}
                onClick={() => handleModeChange('tiptap')}
              >
                🎨 Rich Editor (Tiptap)
              </button> */}
            </div>
            <div className="editor-actions">
              <button
                onClick={handleCancel}
                className="cancel-btn"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="save-btn"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : '💾 Save Changes'}
              </button>
            </div>
          </div>

          <div className="editor-content">
            {editorMode === 'simple' ? (
              <div className="simple-editor-with-preview">
                <div className="editor-hint">
                  💡 <strong>Simple Editor:</strong> Edit raw HTML with helpful tools and live preview. Perfect for quick edits and precise control.
                  <HelpPopup title="Simple HTML Editor Guide" position="bottom">
                    <p><strong>Simple Editor</strong> gives you direct access to the HTML code with helpful insertion tools.</p>

                    <p><strong>Key Features:</strong></p>
                    <ul>
                      <li><strong>🔗 Insert Link:</strong> Add links to pages, products, or external URLs</li>
                      <li><strong>📄 Insert Template:</strong> Use pre-built page templates (tune pages, etc.)</li>
                      <li><strong>🖼️ Insert Image:</strong> Add images from your asset library</li>
                      <li><strong>🎵 Insert Audio:</strong> Add audio players from your asset library</li>
                      <li><strong>🎥 Insert Video:</strong> Add video players from your asset library</li>
                      <li><strong>Style/Class:</strong> Add formatted elements with styling</li>
                    </ul>

                    <div className="tip">
                      💡 <strong>Tip:</strong> The live preview updates as you type, so you can see exactly how your changes will look!
                    </div>

                    <p><strong>When to Use Simple Editor:</strong></p>
                    <ul>
                      <li>Quick HTML edits</li>
                      <li>Inserting templates</li>
                      <li>Precise control over markup</li>
                      <li>Working with custom HTML/CSS</li>
                    </ul>

                    <p><strong>When to Use Rich Editor:</strong></p>
                    <ul>
                      <li>Visual formatting without HTML</li>
                      <li>Creating tables</li>
                      <li>Complex text formatting</li>
                    </ul>
                  </HelpPopup>
                </div>
                {validationMsg && (
                  <div style={{ color: validationMsg.startsWith('Warning') ? '#eab308' : '#ef4444', marginBottom: 6 }}>
                    {validationMsg}
                  </div>
                )}
                <div className="editor-toolbar">
                  <div className="toolbar-dropdown-group">
                    <button type="button" className="toolbar-btn" onClick={() => setShowLinkPicker(true)} title="Insert Link - Enhanced Picker">
                      🔗 Insert Link
                    </button>
                  </div>
                  <div className="toolbar-dropdown-group" ref={styleDropdownRef}>
                    <button type="button" className="toolbar-btn" onClick={() => setShowStyleDropdown(v => !v)}>
                      Insert Style/Class ▼
                    </button>
                    {showStyleDropdown && (
                      <div className="toolbar-dropdown style-dropdown">
                        {STYLE_OPTIONS.map(opt => (
                          <DropdownItemWithPreview key={opt.label} label={opt.label} example={opt.example} tooltip={opt.tooltip} desc={opt.desc} onClick={() => handleStyleInsert(opt.snippet)} />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="toolbar-dropdown-group">
                    <button
                      type="button"
                      className="toolbar-btn toolbar-btn-highlight"
                      onClick={() => {
                        setAssetSearchType('image');
                        setShowAssetSearchModal(true);
                      }}
                      title="Browse and insert assets from database"
                    >
                      🖼️ Insert Asset
                    </button>
                  </div>
                  <div className="toolbar-dropdown-group" ref={templateDropdownRef}>
                    <button
                      type="button"
                      className="toolbar-btn"
                      onClick={() => setShowTemplateDropdown(v => !v)}
                      disabled={templateSource === 'loading'}
                      title={
                        templateSource === 'loading' ? 'Loading templates…' :
                        templateSource === 'db'      ? 'Templates loaded from Admin DB' :
                                                       'Using built-in templates (not seeded in Admin yet)'
                      }
                    >
                      📄 Insert Template{templateSource === 'loading' ? ' …' : ' ▼'}
                    </button>
                    {showTemplateDropdown && (
                      <div className="toolbar-dropdown template-dropdown">
                        {/* Source badge */}
                        <div className={`template-source-badge ${templateSource}`}>
                          {templateSource === 'db'
                            ? '📦 Templates from Admin DB'
                            : '⚠ Using built-in defaults — seed in Admin → Page Templates'}
                        </div>

                        {Object.entries(activeTemplates).map(([key, template]) => (
                          <div key={key} className="dropdown-item" onClick={() => handleTemplateInsert(key)}>
                            <span className="template-icon">
                              {key.includes('tune') ? '🎵' : key.includes('product') ? '📚' : '📝'}
                            </span>
                            <span className="dropdown-label-group">
                              <span className="dropdown-label-main">{template.name}</span>
                              <span className="dropdown-label-desc">{template.description}</span>
                            </span>
                          </div>
                        ))}

                        {/* Manage link */}
                        <div className="template-manage-link">
                          <a href="/admin/templates" target="_blank" rel="noopener noreferrer">
                            ⚙ Manage Templates
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="simple-editor-split">
                  <div className="editor-panel">
                    <div className="panel-header">
                      <h4>📝 HTML Editor</h4>
                    </div>
                    <textarea
                      ref={textareaRef}
                      value={simpleEditorContent}
                      onChange={handleTextareaChange}
                      className="simple-textarea"
                      placeholder="Edit HTML content..."
                      style={{ minHeight: '400px' }}
                    />
                  </div>
                  <div className="preview-panel">
                    <div className="panel-header">
                      <h4>👁️ Live Preview</h4>
                    </div>
                    <div className="preview-content">
                      <div
                        dangerouslySetInnerHTML={{ __html: contentHtml || '<p><em>Start typing HTML to see the preview...</em></p>' }}
                        className="preview-html"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="tiptap-editor">
                <div className="editor-hint">
                  💡 <strong>Rich Editor (Tiptap):</strong> Full WYSIWYG editing with formatting toolbar. Great for complex layouts and rich content.
                </div>
                <div className="tiptap-toolbar">
                  <button
                    onClick={() => tiptapEditor?.chain().focus().toggleBold().run()}
                    className={tiptapEditor?.isActive('bold') ? 'is-active' : ''}
                    title="Bold"
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    onClick={() => tiptapEditor?.chain().focus().toggleItalic().run()}
                    className={tiptapEditor?.isActive('italic') ? 'is-active' : ''}
                    title="Italic"
                  >
                    <em>I</em>
                  </button>
                  <button
                    onClick={() => tiptapEditor?.chain().focus().toggleHeading({ level: 1 }).run()}
                    className={tiptapEditor?.isActive('heading', { level: 1 }) ? 'is-active' : ''}
                    title="Heading 1"
                  >
                    H1
                  </button>
                  <button
                    onClick={() => tiptapEditor?.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={tiptapEditor?.isActive('heading', { level: 2 }) ? 'is-active' : ''}
                    title="Heading 2"
                  >
                    H2
                  </button>
                  <button
                    onClick={() => tiptapEditor?.chain().focus().toggleHeading({ level: 3 }).run()}
                    className={tiptapEditor?.isActive('heading', { level: 3 }) ? 'is-active' : ''}
                    title="Heading 3"
                  >
                    H3
                  </button>
                  <button
                    onClick={() => tiptapEditor?.chain().focus().toggleBulletList().run()}
                    className={tiptapEditor?.isActive('bulletList') ? 'is-active' : ''}
                    title="Bullet List"
                  >
                    • List
                  </button>
                  <button
                    onClick={() => tiptapEditor?.chain().focus().toggleOrderedList().run()}
                    className={tiptapEditor?.isActive('orderedList') ? 'is-active' : ''}
                    title="Numbered List"
                  >
                    1. List
                  </button>
                  <button
                    onClick={() => tiptapEditor?.chain().focus().toggleBlockquote().run()}
                    className={tiptapEditor?.isActive('blockquote') ? 'is-active' : ''}
                    title="Quote"
                  >
                    Quote
                  </button>
                  <button
                    onClick={() => tiptapEditor?.chain().focus().setHorizontalRule().run()}
                    title="Horizontal Rule"
                  >
                    HR
                  </button>
                  <button
                    onClick={() => tiptapEditor?.chain().focus().undo().run()}
                    disabled={!tiptapEditor?.can().undo()}
                    title="Undo"
                  >
                    ↶ Undo
                  </button>
                  <button
                    onClick={() => tiptapEditor?.chain().focus().redo().run()}
                    disabled={!tiptapEditor?.can().redo()}
                    title="Redo"
                  >
                    ↷ Redo
                  </button>
                </div>
                <div className="tiptap-content">
                  <EditorContent editor={tiptapEditor} />
                </div>
              </div>
            )}
          </div>

          <div className="editor-footer">
            <p className="editor-hint">
              💡 <strong>Tip:</strong> Switch between editors anytime. Both save to the same content.
              Use Simple Editor for quick HTML edits, Rich Editor for complex formatting.
            </p>
          </div>
        </div>
      ) : (
        <div className="display-content">
          <div dangerouslySetInnerHTML={{ __html: contentHtml || initialHtml || '' }} />
          {canEdit && (
            <button
              className="edit-icon-button"
              onClick={handleEditClick}
              title="Edit Content"
            >
              ✏️ HTML Editor
            </button>
          )}
        </div>
      )}

      {/* Asset Search Modal */}
      <AssetSearchModal
        isOpen={showAssetSearchModal}
        onClose={() => setShowAssetSearchModal(false)}
        onSelectAsset={handleAssetSelect}
        fileType={assetSearchType}
      />

      {/* Link Picker Modal */}
      <LinkPickerModal
        isOpen={showLinkPicker}
        onClose={() => setShowLinkPicker(false)}
        onInsertLink={handleLinkPickerInsert}
      />

      {/* Template Insert Modal */}
      {showTemplateModal && selectedTemplate && (
        <TemplateInsertModal
          template={selectedTemplate}
          onInsert={handleTemplateModalInsert}
          onCancel={handleTemplateModalCancel}
        />
      )}
    </div>
  );
};

export default EditableContentDual;
